import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SafeIcon from '../common/SafeIcon';
import * as LuIcons from 'react-icons/lu';

const { 
  LuHouse, 
  LuWrench, 
  LuCalendar, 
  LuMenu, 
  LuX, 
  LuGlobe, 
  LuAward, 
  LuCircleHelp,
  LuInfo 
} = LuIcons;

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const sections = ['hero', 'showcase', 'services', 'about', 'faq', 'contact'];
      const current = sections.find(id => {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'hero', label: t('nav.home'), icon: LuHouse },
    { id: 'showcase', label: t('nav.showcase'), icon: LuAward },
    { id: 'services', label: t('nav.services'), icon: LuWrench },
    { id: 'about', label: t('nav.about'), icon: LuInfo },
    { id: 'faq', label: t('nav.faq'), icon: LuCircleHelp },
    { id: 'contact', label: t('nav.book'), icon: LuCalendar },
  ];

  return (
    <>
      <motion.nav 
        className={`navbar ${scrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        <div className="nav-container">
          <a href="#hero" className="nav-logo">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/25fe57c277fc4d49baa2815a1646d92e367cb2ce89a7d2c278a4f8202228d92c658a999133b0fffe7649687a42276768/images/aW1hZ2UvcG5n/80a798bdc470f1a4284e1fcfb2b8a9e6.png" 
              alt="Verkstad360 Logo" 
              className="logo-image" 
            />
          </a>

          <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="nav-links desktop">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                >
                  <SafeIcon icon={item.icon} className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                  {activeSection === item.id && (
                    <motion.div 
                      className="active-background"
                      layoutId="navHighlight"
                    />
                  )}
                </a>
              ))}
            </div>

            <div className="nav-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button 
                className="lang-switcher"
                onClick={() => i18n.changeLanguage(i18n.language.startsWith('sv') ? 'en' : 'sv')}
              >
                <SafeIcon icon={LuGlobe} />
                <span>{i18n.language.toUpperCase().substring(0, 2)}</span>
              </button>

              <button className="mobile-menu-toggle" onClick={() => setIsOpen(!isOpen)}>
                <SafeIcon icon={isOpen ? LuX : LuMenu} />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div 
              className="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              onClick={e => e.stopPropagation()}
            >
              {navItems.map(item => (
                <a 
                  key={item.id} 
                  href={`#${item.id}`} 
                  className="mobile-nav-link"
                  onClick={() => setIsOpen(false)}
                >
                  <SafeIcon icon={item.icon} />
                  <span>{item.label}</span>
                </a>
              ))}
              
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: 0.4s;
          padding: 8px 0;
        }

        .navbar.scrolled {
          background: rgba(7, 7, 14, 0.85);
          backdrop-filter: blur(24px);
          border-bottom: 1px solid rgba(221, 219, 230, 0.08);
        }

        .nav-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          text-decoration: none;
        }

        .logo-image {
          height: 59px; /* Reduced by 30% from 84px */
          width: auto;
          object-fit: contain;
          border-radius: 6px;
        }

        .nav-links.desktop {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          background: rgba(221, 219, 230, 0.04);
          padding: 0.4rem;
          border-radius: 16px;
          border: 1px solid rgba(221, 219, 230, 0.1);
        }

        .nav-link {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.6rem 1.2rem;
          border-radius: 12px;
          color: #848489;
          text-decoration: none;
          transition: 0.3s;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .nav-link.active {
          color: white;
        }

        .active-background {
          position: absolute;
          inset: 0;
          background: rgba(131, 119, 190, 0.2);
          border-radius: 12px;
          z-index: -1;
          border: 1px solid rgba(131, 119, 190, 0.3);
        }

        .lang-switcher {
          background: rgba(221, 219, 230, 0.04);
          border: 1px solid rgba(221, 219, 230, 0.1);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .mobile-menu-toggle {
          display: none;
          background: rgba(221, 219, 230, 0.05);
          border: 1px solid rgba(221, 219, 230, 0.1);
          color: white;
          padding: 0.6rem;
          border-radius: 10px;
          cursor: pointer;
          font-size: 1.1rem;
        }

        .mobile-menu-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          z-index: 1001;
          display: flex;
          justify-content: flex-end;
        }

        .mobile-menu {
          width: 260px;
          background: #07070E;
          height: 100%;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .mobile-nav-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          color: #848489;
          text-decoration: none;
          padding: 1rem;
          border-radius: 10px;
        }

        @media (max-width: 1024px) {
          .logo-image { height: 42px; } /* Reduced by 30% from 60px */
        }

        @media (max-width: 768px) {
          .nav-links.desktop { display: none; }
          .mobile-menu-toggle { display: block; }
          .logo-image { height: 36px; } /* Reduced by 30% from 52px */
        }
      `}</style>
    </>
  );
};

export default Navigation;